import React, { useState, useEffect, useMemo, useCallback, useRef, memo } from 'react';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Calculator,
  Info,
  Bot,
} from 'lucide-react';
import { formatPriceWithoutCurrency } from '../utils/priceFormatter';
import { getAssetDescription } from '../utils/assetDescriptions';
import { useTheme } from '../contexts/ThemeContext';
import { useOrderContext } from '../contexts/OrderContext';
import { useToast } from '../contexts/ToastContext';
import { useTradingError } from '../contexts/TradingErrorContext';
import { useBackendAITrading } from '../contexts/BackendAITradingContext';
import NumberInput from './NumberInput';
import OrderConfirmationModal from './OrderConfirmationModal';
import AITradePanel from './ai-trading/AITradePanel';
import { mobilePerformanceOptimizer } from '../utils/mobilePerformanceOptimizer';

interface TradingFormProps {
  symbol: string;
  currentPrice?: string;
}

const TradingForm: React.FC<TradingFormProps> = ({ symbol, currentPrice }) => {
  const [orderType, setOrderType] = useState<'market' | 'limit' | 'stop'>(
    'limit'
  );
  const [side, setSide] = useState<'buy' | 'sell'>('buy');
  const [amount, setAmount] = useState('');
  const [price, setPrice] = useState('');
  const [stopPrice, setStopPrice] = useState('');
  const [leverage, setLeverage] = useState('100');
  const [orderSize, setOrderSize] = useState('');
  const [tradeMode, setTradeMode] = useState<'manual' | 'ai'>('ai');
  
  // Optimize trade mode switching with useCallback
  const handleTradeModeChange = useCallback((mode: 'manual' | 'ai') => {
    setTradeMode(mode);
  }, []);
  const [leverageDropdownOpen, setLeverageDropdownOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<'bottom' | 'top'>('bottom');
  const [priceError, setPriceError] = useState('');
  const [amountError, setAmountError] = useState('');
  const { theme } = useTheme();
  const { selectedPrice, setOrderDetails, setShowOrderConfirmation, showConfirmationPopup, executeOrder } = useOrderContext();
  const { setOnRetry } = useTradingError();
  const { showToast } = useToast();
  const { isAIEnabled, activeStrategies, strategies } = useBackendAITrading();

  // Ref for scrollable container
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Function to scroll to bottom of AI trade section - Optimized for mobile
  const scrollToBottom = useCallback(() => {
    if (scrollContainerRef.current) {
      // Use smooth scrolling for better mobile experience
      scrollContainerRef.current.scrollTo({
        top: scrollContainerRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, []);

  const availableBalance = 45678.9;

  // Auto-switch to AI Trade mode when active strategies are found in Firestore
  useEffect(() => {
    // Check if there are any active strategies with isActive=true and aiTradeStartEnabled=true
    const hasActiveStrategy = activeStrategies.some((strategy: any) => 
      strategy.isActive === true && strategy.aiTradeStartEnabled === true
    );

    if (hasActiveStrategy && tradeMode !== 'ai') {
      // console.log('🤖 Auto-switching to AI Trade mode due to active strategy in Firestore');
      setTradeMode('ai');
    }
  }, [activeStrategies, tradeMode]);

  // Determine AI button color based on AI state
  const getAIButtonColor = useCallback(() => {
    if (tradeMode !== 'ai') {
      return 'theme-bg-quaternary theme-text-secondary theme-border hover:theme-bg-quinary hover:theme-text-primary';
    }

    // Check if AI is actively running
    const isAIRunning = activeStrategies.some((strategy: any) => 
      strategy.isActive === true && strategy.aiTradeStartEnabled === true
    );

    // Check if AI is available (strategy selected but not running)
    const isAIAvailable = activeStrategies.some((strategy: any) => 
      strategy.aiTradeStartEnabled === true
    );

    if (isAIRunning) {
      // AI is actively running - bright brand green
      return 'theme-bg-quaternary text-[#2AB7A6] border-[#2AB7A6]';
    } else if (isAIAvailable) {
      // AI is available but not running - medium brand green
      return 'theme-bg-quaternary text-[#2AB7A6] border-[#2AB7A6]';
    } else {
      // AI tab is selected but no strategy - default brand green
      return 'theme-bg-quaternary text-[#2AB7A6] border-[#2AB7A6]';
    }
  }, [tradeMode, activeStrategies]);

  // Calculate trading values based on leverage and position size - memoized for performance
  const tradingValues = useMemo(() => {
    const numAmount = parseFloat(amount) || 0;
    const numLeverage = parseFloat(leverage) || 100;
    let numPrice: number;
    
    if (orderType === 'market') {
      // For market orders, use current live price
      numPrice = parseFloat(currentPrice || '0');
    } else if (orderType === 'limit') {
      // For limit orders, use entered price
      numPrice = parseFloat(price) || 0;
    } else if (orderType === 'stop') {
      // For stop orders, use current price for calculations (execution price)
      numPrice = parseFloat(currentPrice || '0');
    } else {
      numPrice = 0;
    }
    
    const numStopPrice = parseFloat(stopPrice) || 0;
    
    // Position value (notional value)
    const positionValue = numAmount * numPrice;
    
    // Margin required = Position Value / Leverage
    const marginRequired = numLeverage > 0 ? positionValue / numLeverage : positionValue;
    
    // Estimated cost (for limit/stop orders, this is the margin required)
    // For market orders, this is the full position value
    const estimatedCost = orderType === 'market' ? positionValue : marginRequired;
    
    // Available margin (balance available for new positions)
    const availableMargin = availableBalance;
    
    // Margin ratio (how much of available balance this trade will use)
    const marginRatio = availableMargin > 0 ? (marginRequired / availableMargin) * 100 : 0;
    
    return {
      positionValue,
      marginRequired,
      estimatedCost,
      availableMargin,
      marginRatio
    };
  }, [amount, leverage, orderType, currentPrice, price, stopPrice, availableBalance]);

  // Validation functions
  const validatePrice = (priceValue: string): boolean => {
    if (orderType === 'market') return true; // Market orders don't need price validation
    
    if (!priceValue.trim()) {
      setPriceError('Enter a valid price');
      return false;
    }
    
    const numPrice = parseFloat(priceValue);
    if (isNaN(numPrice) || numPrice <= 0) {
      setPriceError('Enter a valid price');
      return false;
    }
    
    setPriceError('');
    return true;
  };

  const validateStopPrice = (stopPriceValue: string): boolean => {
    if (orderType !== 'stop') return true; // Only validate for stop orders
    
    if (!stopPriceValue.trim()) {
      return false; // Stop price is required for stop orders
    }
    
    const numStopPrice = parseFloat(stopPriceValue);
    if (isNaN(numStopPrice) || numStopPrice <= 0) {
      return false;
    }
    
    return true;
  };

  const validateAmount = (amountValue: string): boolean => {
    if (!amountValue.trim()) {
      setAmountError('Amount is required');
      return false;
    }
    
    const numAmount = parseFloat(amountValue);
    if (isNaN(numAmount) || numAmount <= 0) {
      setAmountError('Not a valid amount');
      return false;
    }
    
    setAmountError('');
    return true;
  };

  const validateLeverage = (leverageValue: string): boolean => {
    const numLeverage = parseFloat(leverageValue);
    if (isNaN(numLeverage) || numLeverage < 1 || numLeverage > 100) {
      return false;
    }
    return true;
  };

  const validateMargin = (): boolean => {
    const { marginRequired, availableMargin } = tradingValues;
    
    // Check if margin required exceeds available balance
    if (marginRequired > availableMargin) {
      setAmountError('Insufficient margin. Reduce position size or leverage.');
      return false;
    }
    
    // Check if margin ratio is too high (warn if > 80%)
    if (tradingValues.marginRatio > 80) {
      setAmountError('High margin usage. Consider reducing position size.');
      return false;
    }
    
    return true;
  };



  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear previous errors
    setPriceError('');
    setAmountError('');
    
    // Validate inputs
    const isPriceValid = validatePrice(price);
    const isAmountValid = validateAmount(amount);
    const isStopPriceValid = validateStopPrice(stopPrice);
    const isLeverageValid = validateLeverage(leverage);
    const isMarginValid = validateMargin();
    
    if (!isPriceValid || !isAmountValid || !isStopPriceValid || !isLeverageValid || !isMarginValid) {
      return; // Stop submission if validation fails
    }
    
    // Determine the price to use
    let orderPrice: number;
    let stopPriceValue: number | undefined;
    
    if (orderType === 'market') {
      // For market orders, use current live price
      orderPrice = parseFloat(currentPrice || '0');
    } else if (orderType === 'limit') {
      // For limit orders, use entered price as execution price
      orderPrice = parseFloat(price);
    } else if (orderType === 'stop') {
      // For stop orders, use current price as execution price and stopPrice as trigger
      orderPrice = parseFloat(currentPrice || '0');
      stopPriceValue = parseFloat(stopPrice);
    } else {
      // Fallback case
      orderPrice = parseFloat(currentPrice || '0');
    }
    
    // Prepare order details for confirmation
    const orderDetails = {
      side,
      type: orderType,
      symbol,
      price: orderPrice,
      stopPrice: stopPriceValue,
      amount: parseFloat(amount),
      leverage: parseInt(leverage),
    };
    
    
    // Check if confirmation popup is enabled
    if (showConfirmationPopup) {
      // Show confirmation modal
      setOrderDetails(orderDetails);
      setShowOrderConfirmation(true);
    } else {
      // Set up retry functionality
      setOnRetry(() => () => handleSubmit(e));
      
      // Execute order directly without confirmation
      setOrderDetails(orderDetails);
      const result = await executeOrder(orderDetails);
      if (result.success) {
        // Show success toast with order details
        const orderMessage = `${orderDetails.side.toUpperCase()} ${orderDetails.amount} ${orderDetails.symbol} at ${orderDetails.price}`;
        showToast(orderMessage, orderDetails.side as 'buy' | 'sell');
      }
      // Error handling is now done in the OrderContext via TradingErrorContext
    }
  }, [price, amount, stopPrice, leverage, orderType, currentPrice, side, symbol, showConfirmationPopup, setOrderDetails, setShowOrderConfirmation, executeOrder, showToast, setOnRetry]);

  const quickAmounts = useMemo(() => [0.05, 0.1, 1.10], []);



  // Update price field when selectedPrice changes - debounced for performance
  useEffect(() => {
    if (selectedPrice) {
      const timeoutId = setTimeout(() => {
        if (orderType === 'limit') {
          setPrice(selectedPrice.toString());
        } else if (orderType === 'stop') {
          setStopPrice(selectedPrice.toString());
        }
      }, 100); // 100ms debounce
      
      return () => clearTimeout(timeoutId);
    }
  }, [selectedPrice, orderType]);

  // Real-time validation for price field
  useEffect(() => {
    if (price && orderType !== 'market') {
      validatePrice(price);
    }
  }, [price, orderType]);

  // Real-time validation for amount field
  useEffect(() => {
    if (amount) {
      validateAmount(amount);
    }
  }, [amount]);

  // Real-time validation for leverage field
  useEffect(() => {
    if (leverage) {
      if (!validateLeverage(leverage)) {
        setAmountError('Leverage must be between 1x and 100x');
      } else {
        setAmountError('');
      }
    }
  }, [leverage]);

  // Real-time validation for margin requirements
  useEffect(() => {
    if (amount && leverage && (price || orderType === 'market')) {
      validateMargin();
    }
  }, [amount, leverage, price, orderType, currentPrice]);

  // Function to calculate dropdown position based on available space
  const calculateDropdownPosition = (buttonElement: HTMLElement) => {
    const rect = buttonElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const dropdownHeight = 200; // Approximate height of dropdown with 7 options
    
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    
    // If there's not enough space below but enough space above, open upward
    if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
      return 'top';
    }
    
    // Default to bottom if there's enough space below or more space below than above
    return 'bottom';
  };

  // Handle dropdown toggle with position calculation
  const handleLeverageDropdownToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!leverageDropdownOpen) {
      // Calculate position when opening
      const buttonElement = document.querySelector('.leverage-dropdown-button') as HTMLElement;
      if (buttonElement) {
        const position = calculateDropdownPosition(buttonElement);
        setDropdownPosition(position);
      }
    }
    setLeverageDropdownOpen(!leverageDropdownOpen);
  };

  // Close leverage dropdown when clicking outside - Optimized
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (leverageDropdownOpen && !target.closest('.leverage-dropdown-container')) {
        setLeverageDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside, { passive: true });
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [leverageDropdownOpen]);

  // Recalculate dropdown position on window resize - Optimized with debouncing
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    const handleResize = () => {
      // Debounce resize events for better performance
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        if (leverageDropdownOpen) {
          const buttonElement = document.querySelector('.leverage-dropdown-button') as HTMLElement;
          if (buttonElement) {
            const position = calculateDropdownPosition(buttonElement);
            setDropdownPosition(position);
          }
        }
      }, 100); // 100ms debounce
    };

    window.addEventListener('resize', handleResize, { passive: true });
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
    };
  }, [leverageDropdownOpen]);

  // Cleanup effect to prevent memory leaks
  useEffect(() => {
    return () => {
      // Cleanup any pending operations
      mobilePerformanceOptimizer.cleanup();
    };
  }, []);

  return (
    <div className={`card p-2 md:p-4 border border-gray-200 ${theme === 'light' ? 'bg-white border border-0 border-gray-100' : 'border-gray-900'}`}>
      {/* Asset Information and Trade Mode Selection */}
      <div className="mb-4">
        {/* Asset Name and Trade Mode Buttons - First row */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-2 space-y-2 md:space-y-0">
          <div>
            <div className="text-xs font-semibold theme-text-primary">{symbol}</div>
            <div className="text-xs theme-text-quaternary">{getAssetDescription(symbol)}</div>
          </div>
          <div className="flex space-x-1 w-full md:w-auto">
            <button
              onClick={() => handleTradeModeChange('manual')}
              className={`flex-1 md:flex-none px-2 py-1 text-xs font-bold rounded-md transition-colors duration-200 border text-center me-1 ${
                tradeMode === 'manual'
                  ? 'theme-bg-quaternary text-[#2AB7A6] border-[#2AB7A6]'
                  : 'theme-bg-quaternary theme-text-secondary theme-border hover:theme-bg-quinary hover:theme-text-primary'
              }`}
            >
              <div className="leading-tight">
                <div>Manual</div>
                <div>Trade</div>
              </div>
            </button>
            <button
              onClick={() => handleTradeModeChange('ai')}
              className={`flex-1 md:flex-none px-2 py-1 text-xs font-bold rounded-md transition-colors duration-200 border text-center ${getAIButtonColor()}`}
            >
              <div className="leading-tight">
                <div>AI</div>
                <div>Trade</div>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Buy/Sell Tabs */}
        {tradeMode !== 'ai' && (
          <div className={`flex rounded-lg p-1 mb-4 px-0 ${theme === 'light' ? 'bg-gray-100 border border-gray-200' : 'theme-bg-quaternary'}`}>
          <button
            onClick={() => setSide('buy')}
            className={`flex-1 py-2 md:py-1 px-4 rounded-md text-xs font-medium transition-colors duration-200 ${
              side === 'buy'
                ? 'bg-[#2AB7A6] text-white'
                : 'theme-text-quaternary hover:theme-text-primary'
            } ${theme === 'light' ? 'ms-1' : ''}`}
          >
            <TrendingUp className="h-4 w-4 inline mr-2" />
            Buy
          </button>
          <button
            onClick={() => setSide('sell')}
            className={`flex-1 py-2 md:py-1 px-4 rounded-md text-xs font-medium transition-colors duration-200 ${
              side === 'sell'
                ? 'bg-red-600 text-white'
                : 'theme-text-quaternary hover:theme-text-primary'
            } ${theme === 'light' ? 'me-1' : ''}`}
          >
            <TrendingDown className="h-4 w-4 inline mr-2" />
            Sell
          </button>
        </div>
        )}       


      {/* Scrollable Form Content - Optimized for mobile performance */}
      <div 
        ref={scrollContainerRef}
        className="overflow-y-auto scrollbar-hide" 
        style={{ 
          scrollbarWidth: 'none', 
          msOverflowStyle: 'none',
          // `100vh - Npx` alone grows one-for-one with the screen, so this panel used to claim
          // every extra pixel a taller monitor offered and the asset list above it was left
          // with the same ~250px whatever the screen size. The second term caps that at half
          // of the additional height, leaving the other half for the list. The two terms are
          // equal at a 1080px-tall viewport and `min()` takes the smaller, so nothing below
          // that height changes at all.
          maxHeight: tradeMode === 'ai'
            ? 'min(100vh - 475px, 50vh + 65px)'
            : 'min(100vh - 525px, 50vh + 15px)',
          minHeight: tradeMode === 'ai'
            ? 'min(100vh - 531px, 50vh + 9px)'
            : 'min(100vh - 525px, 50vh + 15px)',
          // Mobile performance optimizations
          WebkitOverflowScrolling: 'touch',
          transform: 'translateZ(0)', // Enable hardware acceleration
          willChange: 'scroll-position'
        }}
      >
        {tradeMode === 'ai' ? (
          <AITradePanel 
            symbol={symbol} 
            currentPrice={currentPrice} 
            onTradeExecuted={scrollToBottom}
          />
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
          {/* Order Type */}
             <div className="px-0">
              {/* Market Order Info */}
              {orderType === 'market' && (
                <div className="mb-2 p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded text-xs text-blue-700 dark:text-blue-300 hidden">
                  Market orders will execute at the current live price: <span className="font-medium">{currentPrice || '0.00'}</span>
                </div>
              )}
              
              <div className="flex justify-between">
                {[
                  { value: 'limit', label: 'Limit' },
                  { value: 'market', label: 'Market' },
                  { value: 'stop', label: 'Stop' }
                ].map((type) => (
                  <label key={type.value} className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="orderType"
                      value={type.value}
                      checked={orderType === type.value}
                      onChange={(e) => setOrderType(e.target.value as 'market' | 'limit' | 'stop')}
                      className="sr-only"
                    />
                    <div className={`w-4 h-4 rounded-full border-2 transition-all duration-200 ${
                      orderType === type.value
                        ? 'border-[#2AB7A6] bg-[#2AB7A6]'
                        : theme === 'light' ? 'border-gray-300 bg-transparent' : 'theme-border bg-transparent'
                    }`}>
                      {orderType === type.value && (
                        <div className="w-2 h-2 rounded-full bg-white m-auto mt-0.5"></div>
                      )}
                    </div>
                    <span className={`ml-2 text-xs font-medium transition-colors duration-200 ${
                      orderType === type.value
                        ? 'text-[#2AB7A6]'
                        : 'theme-text-quaternary'
                    }`}>
                      {type.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Price Fields */}
            {orderType === 'limit' && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-medium theme-text-secondary">
                    Price (USD)
                  </label>
                  <button
                    type="button"
                    onClick={() => setPrice(currentPrice || '0.00')}
                    className="text-[11px] theme-text-quaternary hover:text-[#2AB7A6] transition-colors duration-200 cursor-pointer"
                    title="Click to fill current price"
                  >
                    <span>$</span> 
                    <span dangerouslySetInnerHTML={{ __html: formatPriceWithoutCurrency(currentPrice || '0.00', symbol) }} />
                  </button>
                </div>
                <NumberInput
                  value={price}
                  onChange={e => setPrice(e.target.value)}
                  placeholder="Enter price"
                  error={!!priceError}
                  step="0.01"
                  min="0"
                  className="!p-1.5 w-full"
                />
                {priceError && (
                  <div className="text-red-500 text-xs mt-1">{priceError}</div>
                )}
              </div>
            )}

            {orderType === 'stop' && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-medium theme-text-secondary">
                    Stop Price (USD)
                  </label>
                  <button
                    type="button"
                    onClick={() => setStopPrice(currentPrice || '0.00')}
                    className="text-[11px] theme-text-quaternary hover:text-[#2AB7A6] transition-colors duration-200 cursor-pointer"
                    title="Click to fill current price"
                  >
                    <span>$</span> 
                    <span dangerouslySetInnerHTML={{ __html: formatPriceWithoutCurrency(currentPrice || '0.00', symbol) }} />
                  </button>
                </div>
                <NumberInput
                  value={stopPrice}
                  onChange={e => setStopPrice(e.target.value)}
                  placeholder="Enter stop price"
                  step="0.01"
                  min="0"
                />
              </div>
            )}

                    {/* Amount */}
            <div>
              <label className="block text-xs font-medium theme-text-secondary mb-2">
                Amount ({symbol.split('/')[0]})
              </label>
              <NumberInput
                value={amount}
                onChange={e => setAmount(e.target.value)}
                placeholder="Enter amount"
                error={!!amountError}
                step="0.01"
                min="0"
                className="!p-1.5 w-full"
              />
              {amountError && (
                <div className="text-red-500 text-xs mt-1">{amountError}</div>
              )}
              <div className="flex items-center justify-between mt-2">
                <div className="flex space-x-2">
                  {quickAmounts.map(quickAmount => (
                    <button
                      key={quickAmount}
                      type="button"
                      onClick={() => setAmount(quickAmount.toString())}
                      className={`px-2 py-1 text-xs rounded transition-colors duration-200 ${
                        theme === 'light' 
                          ? 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200' 
                          : 'theme-bg-quaternary theme-text-secondary hover:theme-bg-quinary'
                      }`}
                    >
                      {quickAmount}
                    </button>
                  ))}
                </div>
                <div className="relative leverage-dropdown-container">
                  <button
                    onClick={handleLeverageDropdownToggle}
                    className={`leverage-dropdown-button flex items-center space-x-1 px-2 py-1 text-xs font-medium transition-colors duration-200 border rounded-lg focus:outline-none w-16 justify-center ${
                      leverageDropdownOpen
                        ? theme === 'light' 
                          ? 'text-[#2AB7A6] bg-gray-50 border-gray-300'
                          : 'text-[#2AB7A6] theme-border'
                        : theme === 'light' 
                          ? 'text-gray-700 hover:text-[#2AB7A6] bg-gray-50 border-gray-300' 
                          : 'theme-text-secondary hover:text-[#2AB7A6] theme-border'
                    }`}
                  >
                    <span>{leverage}x</span>
                    <svg
                      className={`w-3 h-3 transition-transform duration-200 ${leverageDropdownOpen ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>

                  {/* Leverage Dropdown */}
                  {leverageDropdownOpen && (
                    <div 
                      className={`absolute right-0 z-50 w-20 origin-top-right rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none p-2 ${
                        dropdownPosition === 'top' ? 'bottom-full mb-1' : 'top-full mt-1'
                      } ${theme === 'light' ? 'bg-white border border-gray-200' : 'theme-bg-quaternary'}`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                    >
                      <div className="py-1">
                        {[1, 2, 5, 10, 20, 50, 100].map(leverageValue => (
                          <button
                            key={leverageValue}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setLeverage(leverageValue.toString());
                              setLeverageDropdownOpen(false);
                            }}
                            className={`w-full text-center px-2 py-1 text-xs transition-colors duration-200 rounded ${
                              leverage === leverageValue.toString()
                                ? theme === 'light'
                                  ? 'bg-gray-600 text-white'
                                  : 'bg-gray-500 text-white'
                                : theme === 'light' 
                                  ? 'text-gray-700 hover:bg-gray-200 hover:text-white' 
                                  : 'theme-text-secondary hover:bg-gray-700 hover:text-white'
                            }`}
                          >
                            {leverageValue}x
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className={`w-full py-2 px-3 rounded-lg text-xs transition-colors duration-200 ${
                side === 'buy'
                  ? 'bg-[#2AB7A6] hover:bg-[#25A395] text-white'
                  : 'bg-red-600 hover:bg-red-700 text-white'
              }`}
            >
              {side === 'buy' ? 'Buy' : 'Sell'} {symbol} ({orderType.charAt(0).toUpperCase() + orderType.slice(1)})
            </button>

                    {/* Order Summary */}
            <div className="theme-bg-quaternary rounded-lg p-2 space-y-2 theme-border border">
              {/* Order Summary Header - For both themes */}
              <div className="text-xs font-medium mb-0 theme-text-primary">
                Order Summary
              </div>
            </div>
            <div className="theme-bg-quaternary rounded-lg p-2 space-y-2 theme-border border">              
              {/* Current Price */}
              <div className="flex justify-between text-xs">
                <span className="theme-text-quaternary">Current Price:</span>
                <span className="theme-text-primary text-[11px]">
                  <span dangerouslySetInnerHTML={{ __html: formatPriceWithoutCurrency(currentPrice || '0.00', symbol) }} />
                </span>
              </div>
              
              {/* Stop Price - Only show for stop orders */}
              {orderType === 'stop' && stopPrice && (
                <div className="flex justify-between text-xs">
                  <span className="theme-text-quaternary">Stop Price:</span>
                  <span className="theme-text-primary text-[11px]">
                    <span dangerouslySetInnerHTML={{ __html: formatPriceWithoutCurrency(stopPrice, symbol) }} />
                  </span>
                </div>
              )}
              
              {/* Estimated Cost */}
              <div className="flex justify-between text-xs">
                <span className="theme-text-quaternary">Estimated Cost:</span>
                <span className="theme-text-primary text-[11px]">
                  ${tradingValues.estimatedCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
              
              {/* Margin Required */}
              <div className="flex justify-between text-xs">
                <span className="theme-text-quaternary">Margin Required:</span>
                <span className={`theme-text-primary text-[11px] ${tradingValues.marginRatio > 80 ? 'text-yellow-400' : tradingValues.marginRatio > 95 ? 'text-red-400' : ''}`}>
                  ${tradingValues.marginRequired.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
              
              {/* Position Value */}
              <div className="flex justify-between text-xs">
                <span className="theme-text-quaternary">Position Value:</span>
                <span className="theme-text-primary text-[11px]">
                  ${tradingValues.positionValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
              
              {/* Margin Usage */}
              <div className="flex justify-between text-xs">
                <span className="theme-text-quaternary">Margin Usage:</span>
                <span className={`theme-text-primary text-[11px] ${tradingValues.marginRatio > 80 ? 'text-yellow-400' : tradingValues.marginRatio > 95 ? 'text-red-400' : ''}`}>
                  {tradingValues.marginRatio.toFixed(1)}%
                </span>
              </div>
            </div>

            {/* Toggle Right Panel Button */}
            <button
              type="button"
              onClick={() => {
                // Method 1: Try to call the global function
                if ((window as any).toggleRightPanel) {
                  (window as any).toggleRightPanel();
                  return;
                }
                
                // Method 2: Try to dispatch a custom event
                try {
                  window.dispatchEvent(new CustomEvent('toggleRightPanel'));
                  return;
                } catch (error) {
                  // Event dispatch failed
                }
                
                // Method 3: Try to find the function after a delay
                setTimeout(() => {
                  if ((window as any).toggleRightPanel) {
                    (window as any).toggleRightPanel();
                  } else {
                    // Method 4: Show user-friendly message
                    alert('Right panel toggle is not available. Please navigate to the Trading page first, then return here.');
                  }
                }, 500);
              }}
              className={`w-full py-2 px-3 rounded-lg text-xs transition-colors duration-200 border ${
                theme === 'light' 
                  ? 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200' 
                  : 'theme-bg-quaternary theme-text-secondary theme-border hover:theme-bg-quinary hover:theme-text-primary'
              }`}
            >
              <span className="inline-block align-middle mr-2" style={{ width: 18, height: 18 }}>
                <svg fill="currentColor" viewBox="0 0 640 640" width="18" height="18" style={{ display: 'inline' }}>
                  <path d="M128 160C128 107 171 64 224 64C277 64 320 107 320 160L320 324.7C349.5 351.1 368 389.4 368 432C368 511.5 303.5 576 224 576C144.5 576 80 511.5 80 432C80 389.4 98.5 351 128 324.7L128 160zM224 496C259.3 496 288 467.3 288 432C288 405.1 271.5 382.1 248 372.7L248 280C248 266.7 237.3 256 224 256C210.7 256 200 266.7 200 280L200 372.7C176.5 382.2 160 405.2 160 432C160 467.3 188.7 496 224 496zM416 96L512 96C529.7 96 544 110.3 544 128C544 145.7 529.7 160 512 160L416 160C398.3 160 384 145.7 384 128C384 110.3 398.3 96 416 96zM416 224L512 224C529.7 224 544 238.3 544 256C544 273.7 529.7 288 512 288L416 288C398.3 288 384 273.7 384 256C384 238.3 398.3 224 416 224zM416 384C416 366.3 430.3 352 448 352L512 352C529.7 352 544 366.3 544 384C544 401.7 529.7 416 512 416L448 416C430.3 416 416 401.7 416 384z"/>
                </svg>
              </span>
              Show Order Book Panel
            </button>
          </form>
        )}

        </div>
        
        {/* Order Confirmation Modal */}
        <OrderConfirmationModal />
      </div>
    );
  };

export default memo(TradingForm);
